import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, RotateCcw, Sparkles, Wind, Flame, Mountain } from "lucide-react";

/**
 * Interactive Dosha Self-Assessment
 *
 * 15 questions, each with three options weighted toward Vata / Pitta / Kapha.
 * Result page shows dominant + secondary dosha with tailored recommendations
 * that internally link to the practice's real treatments and articles.
 *
 * No backend required — pure client-side. Later phase can add email capture
 * for a full PDF report.
 */

const Q = [
  {
    q: "Wie ist dein Körperbau?",
    o: [
      { d: "vata", text: "Schlank, feine Knochen, oft zu leicht oder unter­gewichtig" },
      { d: "pitta", text: "Mittelgross, muskulös, angenehmes Verhältnis" },
      { d: "kapha", text: "Kräftig, weiche Konturen, neige zu Gewicht" },
    ],
  },
  {
    q: "Wie fühlt sich deine Haut normalerweise an?",
    o: [
      { d: "vata", text: "Trocken, kühl, dünn, oft rauh" },
      { d: "pitta", text: "Warm, empfindlich, neigt zu Rötungen und Ausschlag" },
      { d: "kapha", text: "Weich, feucht, glatt und dick" },
    ],
  },
  {
    q: "Wie ist dein Haar?",
    o: [
      { d: "vata", text: "Dünn, trocken, gekräuselt oder brüchig" },
      { d: "pitta", text: "Fein, glänzend, früh grauend oder rötlich" },
      { d: "kapha", text: "Dick, dicht, ölig, schwer" },
    ],
  },
  {
    q: "Wie ist deine Verdauung?",
    o: [
      { d: "vata", text: "Unregelmässig, mal Verstopfung, mal weich, oft Blähungen" },
      { d: "pitta", text: "Stark, brauche regelmässige Mahlzeiten, sonst gereizt" },
      { d: "kapha", text: "Langsam aber stabil, kann Mahlzeiten leicht auslassen" },
    ],
  },
  {
    q: "Wie schläfst du?",
    o: [
      { d: "vata", text: "Leicht, oft unterbrochen, viele Träume, brauche wenig Schlaf" },
      { d: "pitta", text: "Mittel, tief, wache früh und ausgeruht auf" },
      { d: "kapha", text: "Tief und lang, morgens schwer aus dem Bett" },
    ],
  },
  {
    q: "Wie ist dein Temperament unter Stress?",
    o: [
      { d: "vata", text: "Ängstlich, unsicher, überfordert" },
      { d: "pitta", text: "Gereizt, ungeduldig, ich attackiere" },
      { d: "kapha", text: "Ich ziehe mich zurück, werde langsam, traurig" },
    ],
  },
  {
    q: "Wie ist deine Beziehung zu Kälte / Wärme?",
    o: [
      { d: "vata", text: "Ich friere schnell, brauche immer Wärme" },
      { d: "pitta", text: "Ich bekomme schnell zu warm, mag lieber Kühle" },
      { d: "kapha", text: "Ich mag Trockenheit und Wärme, kann Kälte gut ab" },
    ],
  },
  {
    q: "Wie ist dein Denkstil?",
    o: [
      { d: "vata", text: "Schnell, kreativ, viele Ideen parallel, vergesse leicht" },
      { d: "pitta", text: "Scharf, analytisch, entscheidungsstark, kritisch" },
      { d: "kapha", text: "Ruhig, überlegt, langsam aber stabil, sehr guter Speicher" },
    ],
  },
  {
    q: "Wie triffst du Entscheidungen?",
    o: [
      { d: "vata", text: "Spontan, wechsele oft die Meinung" },
      { d: "pitta", text: "Klar und schnell, bleibe dabei" },
      { d: "kapha", text: "Langsam, gründlich, mit viel Bedenkzeit" },
    ],
  },
  {
    q: "Wie sprichst du?",
    o: [
      { d: "vata", text: "Schnell, viele Themen, springe" },
      { d: "pitta", text: "Klar, präzise, überzeugend" },
      { d: "kapha", text: "Ruhig, tief, wenig aber überlegt" },
    ],
  },
  {
    q: "Wie sind deine Menstruations­zyklen (falls zutreffend)?",
    o: [
      { d: "vata", text: "Unregelmässig, eher schwach, PMS mit Angst und Krämpfen" },
      { d: "pitta", text: "Regelmässig, stark, PMS mit Reizbarkeit" },
      { d: "kapha", text: "Regelmässig, mittelstark, kaum Beschwerden" },
    ],
  },
  {
    q: "Wie ist deine Ausdauer?",
    o: [
      { d: "vata", text: "Kurze Schübe, dann brauche ich Ruhe" },
      { d: "pitta", text: "Mittel und intensiv, hitze­empfindlich" },
      { d: "kapha", text: "Enorm, ich halte lange und gleichmässig durch" },
    ],
  },
  {
    q: "Was ziehst du dich intuitiv an?",
    o: [
      { d: "vata", text: "Bunte, verspielte, oft trendige Sachen" },
      { d: "pitta", text: "Elegant, hochwertig, sauber, geordnet" },
      { d: "kapha", text: "Bequem, warm, weich, klassisch" },
    ],
  },
  {
    q: "Wie ist dein Verhältnis zu Geld?",
    o: [
      { d: "vata", text: "Impulsiv, kaufe spontan, spare selten" },
      { d: "pitta", text: "Strategisch, investiere gezielt, gebe für Qualität aus" },
      { d: "kapha", text: "Sparsam, sammle, gebe ungern grosse Summen aus" },
    ],
  },
  {
    q: "Wie stehst du zu Veränderungen?",
    o: [
      { d: "vata", text: "Liebe Abwechslung, brauche ständig Neues" },
      { d: "pitta", text: "Wenn sinnvoll, gerne – ich treibe sie an" },
      { d: "kapha", text: "Ich mag Beständigkeit, Veränderungen fallen mir schwer" },
    ],
  },
];

const RESULT_META = {
  vata: {
    label: "Vata",
    icon: Wind,
    color: "text-sky-600 bg-sky-100",
    tagline: "Bewegung und Wind",
    intro:
      "Vata ist die Energie der Bewegung. Kreativität, Ideen und Wandel sind deine Stärken. Wenn Vata aus dem Gleichgewicht gerät, entstehen Unruhe, Schlafprobleme, kalte Extremitäten und Angst.",
    tips: [
      "Halte einen regelmässigen Tages­rhythmus – gleiche Zeiten für Essen und Schlaf",
      "Bevorzuge warme, gekochte, süsse und salzige Speisen mit gesunden Ölen",
      "Meide Rohkost, kalte Getränke und Koffein am Nachmittag",
      "Warme Fussbäder mit Sesamöl vor dem Schlafengehen",
      "Vermeide Überarbeitung und plane bewusst Ruhezeiten ein",
    ],
    recommend: {
      treatments: [
        { name: "Abhyanga Ganzkörper-Ölmassage", href: "/behandlungen/abhyanga", why: "wärmt tief, beruhigt Nervensystem, ideal 1× pro Woche" },
        { name: "Padabhyanga Fussmassage", href: "/massagen", why: "erdet dich, fördert Schlaf – perfekt am Abend" },
        { name: "Klangschalen-Ritual", href: "/klangschalen", why: "synchronisiert Nervensystem, tiefe Ruhe" },
      ],
      articles: [
        { name: "Ayurveda gegen Stress: 5 wirksame Behandlungen", href: "/blog/ayurveda-gegen-stress" },
      ],
    },
  },
  pitta: {
    label: "Pitta",
    icon: Flame,
    color: "text-red-600 bg-red-100",
    tagline: "Feuer und Transformation",
    intro:
      "Pitta ist die Energie der Umwandlung. Zielstrebigkeit, Klarheit und Führungs­stärke sind deine Stärken. Wenn Pitta aus dem Gleichgewicht gerät, entstehen Reizbarkeit, Sodbrennen, Entzündungen und übermässiger Ehrgeiz.",
    tips: [
      "Bevorzuge kühlende, süsse und bittere Speisen: Blattgemüse, Kokos, Milch",
      "Meide Scharfes, Alkohol, viel Kaffee und stark Saures",
      "Plane bewusst Pausen und ‹Nicht-Produktivität› ein",
      "Zeit in der Natur – vor allem an Gewässern – kühlt Pitta",
      "Sanfte statt kompetitive Bewegung – Yoga, Schwimmen, Wandern",
    ],
    recommend: {
      treatments: [
        { name: "Klangschalen-Ritual", href: "/klangschalen", why: "kühlt das innere Feuer, hilft loszulassen" },
        { name: "Shiroabhyanga Kopf­massage", href: "/massagen", why: "beruhigt den analytischen Geist, gut bei Migräne" },
        { name: "Abhyanga mit Kokosöl", href: "/behandlungen/abhyanga", why: "sanft, kühlend – 60 Min tiefe Entspannung" },
      ],
      articles: [
        { name: "Shirodhara: Wirkung und Ablauf", href: "/blog/shirodhara-wirkung-ablauf" },
      ],
    },
  },
  kapha: {
    label: "Kapha",
    icon: Mountain,
    color: "text-emerald-700 bg-emerald-100",
    tagline: "Erde und Wasser",
    intro:
      "Kapha ist die Energie der Struktur. Stabilität, Geduld und liebe­volle Verlässlichkeit sind deine Stärken. Wenn Kapha aus dem Gleichgewicht gerät, entstehen Trägheit, Gewichtszunahme, Verstopfung der Sinne und Schwermut.",
    tips: [
      "Bevorzuge leichte, warme, trockene Speisen: gedünstetes Gemüse, Hülsenfrüchte, wärmende Gewürze",
      "Meide Süsses, Milch­produkte und Fettiges am Abend",
      "Regelmässige, anregende Bewegung – Ausdauersport, Tanzen, Wandern bergauf",
      "Steh früh auf – Kapha-Zeit ist 06-10 Uhr, hier bist du am trägsten",
      "Trockene Bürstenmassage (Garshana) am Morgen aktiviert Lymphe und Stoffwechsel",
    ],
    recommend: {
      treatments: [
        { name: "Garshana Trocken-Peeling", href: "/massagen", why: "aktiviert Lymphfluss und Stoffwechsel, ideal am Morgen" },
        { name: "Abhyanga mit stimulierenden Ölen", href: "/behandlungen/abhyanga", why: "mit warmen Kräuterölen wie Senföl – regt an, statt zu beruhigen" },
        { name: "Ernährungs­beratung", href: "/ernaehrung", why: "der Schlüssel für Kapha – individueller Plan macht messbaren Unterschied" },
      ],
      articles: [
        { name: "Vata, Pitta, Kapha: Welcher Dosha-Typ bist du?", href: "/blog/dosha-typ-test" },
      ],
    },
  },
};

function computeResult(answers) {
  const scores = { vata: 0, pitta: 0, kapha: 0 };
  answers.forEach((a) => {
    if (a) scores[a] += 1;
  });
  const sorted = Object.entries(scores).sort((a, b) => b[1] - a[1]);
  return {
    dominant: sorted[0][0],
    secondary: sorted[1][1] >= sorted[0][1] * 0.7 ? sorted[1][0] : null,
    scores,
  };
}

export default function DoshaTest() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState(Array(Q.length).fill(null));

  useEffect(() => {
    // Fire GA4 event on test start
    if (step === 0 && typeof window !== "undefined" && typeof window.gtag === "function") {
      try {
        window.gtag("event", "dosha_test_start");
      } catch (_) {}
    }
  }, [step]);

  const isDone = step >= Q.length;
  const result = isDone ? computeResult(answers) : null;
  const dominant = result ? RESULT_META[result.dominant] : null;
  const secondary = result?.secondary ? RESULT_META[result.secondary] : null;

  const handleAnswer = (dosha) => {
    const next = [...answers];
    next[step] = dosha;
    setAnswers(next);
    if (step === Q.length - 1) {
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        try {
          window.gtag("event", "dosha_test_complete", { dominant: computeResult(next).dominant });
        } catch (_) {}
      }
    }
    setStep(step + 1);
  };

  const restart = () => {
    setStep(0);
    setAnswers(Array(Q.length).fill(null));
    window.scrollTo(0, 0);
  };

  return (
    <div data-testid="dosha-test-page">
      {/* Header */}
      <section className="bg-sand-soft border-b border-border">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-16 lg:py-20">
          <div className="text-xs uppercase tracking-[0.3em] text-terracotta mb-4">— Dosha Selbst­einschätzung</div>
          <h1 className="font-display font-light text-4xl lg:text-5xl text-ink leading-tight">
            Vata, Pitta oder Kapha – welcher Typ bist du?
          </h1>
          <p className="mt-6 text-ink-muted leading-relaxed max-w-2xl">
            15 kurze Fragen, ca. 3 Minuten. Am Ende erfährst du deinen dominanten Dosha-Typ mit
            konkreten Empfehlungen für Ernährung, Alltag und passenden Behandlungen.
          </p>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-6 lg:px-12 py-16">
        {!isDone && (
          <>
            {/* Progress */}
            <div className="mb-10">
              <div className="flex items-center justify-between text-sm text-ink-muted mb-2">
                <span>Frage {step + 1} von {Q.length}</span>
                <span>{Math.round(((step) / Q.length) * 100)} %</span>
              </div>
              <div className="h-1.5 bg-border rounded-full overflow-hidden">
                <div
                  className="h-full bg-terracotta transition-all duration-300"
                  style={{ width: `${((step) / Q.length) * 100}%` }}
                />
              </div>
            </div>

            <div className="bg-white border border-border rounded-md p-8" data-testid={`quiz-q-${step}`}>
              <h2 className="font-display text-2xl lg:text-3xl text-ink leading-snug mb-8">
                {Q[step].q}
              </h2>
              <div className="space-y-3">
                {Q[step].o.map((opt, i) => (
                  <button
                    key={i}
                    onClick={() => handleAnswer(opt.d)}
                    className="w-full text-left p-5 border border-border rounded-md hover:border-terracotta hover:bg-terracotta/5 transition-colors group"
                    data-testid={`quiz-opt-${step}-${opt.d}`}
                  >
                    <span className="text-ink leading-relaxed group-hover:text-terracotta transition-colors">
                      {opt.text}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {isDone && dominant && (
          <div data-testid="quiz-result">
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${dominant.color} mb-6`}>
              <dominant.icon className="w-4 h-4" strokeWidth={1.5} />
              <span className="text-sm font-medium">Dein dominanter Dosha</span>
            </div>
            <h2 className="font-display text-4xl lg:text-5xl text-ink leading-tight">
              Du bist {secondary ? `${dominant.label}-${secondary.label}` : dominant.label}
            </h2>
            <p className="mt-6 text-ink-muted leading-relaxed">{dominant.intro}</p>
            {secondary && (
              <p className="mt-4 text-ink-muted leading-relaxed">
                Deine <strong className="text-ink">sekundäre Energie</strong> ist {secondary.label} – auch diese
                Anteile machen einen wichtigen Teil deiner Persönlichkeit aus.
              </p>
            )}

            {/* Score visualisation */}
            <div className="mt-10 space-y-4">
              {Object.entries(result.scores).map(([d, s]) => {
                const meta = RESULT_META[d];
                return (
                  <div key={d}>
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="font-medium text-ink flex items-center gap-2">
                        <meta.icon className="w-3.5 h-3.5" strokeWidth={1.5} /> {meta.label}
                      </span>
                      <span className="text-ink-muted">{s} / {Q.length}</span>
                    </div>
                    <div className="h-2 bg-border rounded-full overflow-hidden">
                      <div
                        className={`h-full ${d === "vata" ? "bg-sky-500" : d === "pitta" ? "bg-red-500" : "bg-emerald-500"} transition-all duration-500`}
                        style={{ width: `${(s / Q.length) * 100}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Tips */}
            <section className="mt-12">
              <h3 className="font-display text-2xl text-ink mb-4">Konkrete Empfehlungen für dich</h3>
              <ul className="space-y-3">
                {dominant.tips.map((t, i) => (
                  <li key={i} className="flex gap-3 text-ink-muted leading-relaxed">
                    <span className="text-terracotta font-display">·</span>
                    <span className="text-sm">{t}</span>
                  </li>
                ))}
              </ul>
            </section>

            {/* Treatments */}
            <section className="mt-12">
              <h3 className="font-display text-2xl text-ink mb-6">Passende Behandlungen bei uns</h3>
              <div className="grid gap-4">
                {dominant.recommend.treatments.map((tr, i) => (
                  <Link
                    key={i}
                    to={tr.href}
                    className="group flex items-center justify-between bg-white border border-border rounded-md p-5 hover:border-terracotta transition-colors"
                    data-testid={`quiz-recommend-${i}`}
                  >
                    <div>
                      <div className="font-display text-lg text-ink group-hover:text-terracotta transition-colors">{tr.name}</div>
                      <div className="text-sm text-ink-muted mt-1">{tr.why}</div>
                    </div>
                    <ArrowUpRight className="w-5 h-5 text-terracotta group-hover:rotate-45 transition-transform" strokeWidth={1.5} />
                  </Link>
                ))}
              </div>
            </section>

            {/* CTA */}
            <div className="mt-12 flex flex-wrap gap-4">
              <Link
                to="/booking"
                className="inline-flex items-center gap-2 px-8 py-4 bg-terracotta text-white rounded-full hover:bg-terracotta-hover transition-colors font-medium"
                data-testid="quiz-cta-book"
              >
                <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                Termin für {dominant.label}-Behandlung buchen
              </Link>
              <button
                onClick={restart}
                className="inline-flex items-center gap-2 px-8 py-4 border border-ink text-ink rounded-full hover:bg-ink hover:text-white transition-colors font-medium"
                data-testid="quiz-restart"
              >
                <RotateCcw className="w-4 h-4" strokeWidth={1.5} />
                Test wiederholen
              </button>
            </div>

            <p className="mt-6 text-xs text-ink-muted">
              Diese Selbst­einschätzung ist eine Orientierung, keine ärztliche Diagnose. Für eine
              vertiefte Analyse empfehlen wir ein persönliches <Link to="/ernaehrung" className="text-terracotta underline">Beratungs­gespräch</Link>.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
